// global process
import 'whatwg-fetch';
import Bole from '@haptik/helpers/build/esm/bole';

import BotStore from '@src/store';
import {
  AlertTypes,
  SENTRY_ENVIRONMENTS_ALLOWED,
  GRAPH_NODE_DISPLAY_TEXT,
} from '@src/constants/MogamboConstants';
import {
  receiveAllSubStories,
  receiveStepPriorities,
  receiveUserResponses,
  receiveDisambiguationTexts,
  receiveGeneralResponses,
  receiveNewBot,
  receiveNewStory,
  receiveNewStep,
  receiveSubstoryGraph,
  receiveBotExtract,
  receiveTransferBot,
  receiveDeployBot,
  receivedGeneralNodes,
  updatedGeneralNodes,
  receiveStepPrioritiesPOST,
  receiveGeneralResponsesPOST,
  receiveUpdateBotPOST,
  showRequestFailureAlert,
  receivedNodeSummary,
} from '@src/actions/ApiAction';
import {
  resetData,
  saveGeneralNodes,
  createAlert,
  setABTestInfo,
} from '@src/actions/BotActions';
import {
  BASE_URL,
  ADMIN_BASE_URL,
  ADMIN_ROOT_URL,
  MIX_PANEL,
} from '@src/const';

import * as businessV2 from './businessV2';
import * as botGlossary from './botGlossary';
import * as globalConstants from './globalConstants';

// Utils

import {
  mogamboRequestV2,
  mogamboRequestV3,
  getSelfServeURL,
  appendVersionToURL,
  csrfSafeMethod,
  getCookie,
  sameOrigin,
} from './utils';

export { getSynonyms, getMeanings, setMeanings } from './synonyms';
export { getAllBusinessList, getBusinessPartnerList } from './business';
export {
  searchForEntities,
  saveEntityData,
  getBotResponses,
  saveBotResponses,
  getEntityData,
  searchDictionary,
  saveCRFData,
  saveDictionaryData,
  addLanguageToDictionary,
  uploadDictionary,
  copyEntity,
  setUserSaysEntities,
} from './entities';
export { getStory, updateStory } from './StoryAPI';
export {
  getAllBots,
  createBot,
  fetchTestConfiguration,
  getDomainData,
  addLanguageToBot,
  getBotSummary,
  getBotVersionTimeline,
  getLatestBotVersionsData,
  createOrRestoreVersion,
  updateABTestSplitDetails,
  createABTest,
} from './DomainAPI';
export {
  getPartnerResponsesAsync,
  sendPartnerResponsesAsync,
} from './partner_responses';
export {
  copyPasteBotComponents,
  copyPasteAsync,
  copyPasteTemplateAsync,
} from './CopyPasteAPI';
export { fetchTemplates, updateTemplate } from './TemplatesAPI';
export {
  getMetaInfoAsync,
  getConnectionsToNodeAsync,
  setConnectionsToNodeAsync,
  deleteConnectionsToNodeAsync,
  updateNodeData,
  moveNodesAsync,
  createNode,
  validateGenAiNode,
} from './NodeAPI';

export function MogamboRequest(
  url,
  method,
  data = undefined,
  additionalHeaders = {}
) {
  const wwIva = JSON.parse(localStorage.getItem('ww_iva'));
  const botName = wwIva?.isSmartSkill ? wwIva?.domain_name : wwIva?.title;
  return new Request(url, {
    method,
    headers: new Headers({
      'Content-Type': 'application/json',
      'x-haptik-bot-name': botName,
      ...additionalHeaders,
    }),
    credentials: 'same-origin',
    mode: 'cors',
    cache: 'default',
    body: JSON.stringify(data),
  });
}

export function validateResponse(response) {
  // Check for response status
  if (response.status !== 200) {
    throw new Error('Invalid Response Status');
  }

  // Check for content type
  const contentType = response.headers.get('content-type');
  if (contentType.indexOf('application/json') === -1) {
    throw new Error('Invalid Content Type');
  }

  return response.json();
}

export function authenticate(json) {
  // Check if there was no authentication problem
  // Todo: Redirect to current page instead of Mogambo Home Page
  if (json.not_authenticated) {
    window.location = '/login/?next=/mogambo/';
    return false;
  }
  return true;
}

export function captureException(e) {
  if (SENTRY_ENVIRONMENTS_ALLOWED.includes(process.env.ENV_NAME)) {
    Bole.error(e);
  }
}

export function logout() {
  window.location = '/logout';
}

export function getMetaInfo(key, value, cb) {
  const url = `${BASE_URL}meta/${key}/${value}/`;
  const request = MogamboRequest(url, 'GET');

  fetch(request)
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.indexOf('application/json') !== -1) {
        return response.json().then((json) => {
          authenticate(json);
          if (cb) cb(json.response);
        });
      }

      throw new Error('API request failed!');
    })
    .catch(captureException);
}

export function getAllSubStories(storyId) {
  const url = `${BASE_URL}substory/${storyId}/`;
  const request = MogamboRequest(url, 'GET');

  fetch(request)
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.indexOf('application/json') !== -1) {
        return response.json().then((json) => {
          authenticate(json);
          receiveAllSubStories(json.response);
        });
      }

      resetData();
      throw new Error('API request failed!');
    })
    .catch(captureException);
}

export function getSubstoryGraph(storyId, callback, repositionSkill) {
  const request1 = MogamboRequest(`${BASE_URL}story/graph/${storyId}/`, 'GET');
  const request2 = MogamboRequest(
    `${BASE_URL}sub_story/?story_id=${storyId}`,
    'GET'
  );

  Promise.all([
    fetch(request1).then((response) =>
      response.json().then(
        (json) =>
          new Promise((resolve, reject) => {
            if (json.success) resolve(json.body);
            reject(json.error);
          })
      )
    ),
    fetch(request2).then((response) =>
      response.json().then(
        (json) =>
          new Promise((resolve, reject) => {
            if (json.success) resolve(json.body);
            reject(json.error);
          })
      )
    ),
  ])
    .then(async (values) => {
      const isTourEnabled = localStorage.getItem('isTourEnabled');
      const userDisabledTour = localStorage.getItem('userDisabledTour');

      if (
        values[1].length === 0 &&
        (userDisabledTour === null || isTourEnabled === null)
      ) {
        localStorage.setItem('isTourEnabled', JSON.stringify(true));
      } else if (values[1].length > 0 || (isTourEnabled && userDisabledTour)) {
        localStorage.setItem('isTourEnabled', JSON.stringify(false));
      }

      // repositionSkill is used to change the position of skill on the graph
      // It will be called before we add the values into the store
      if (repositionSkill) {
        values = await repositionSkill(values);
      }

      const abTestingInfo = values[0]?.ab_testing_info;

      receiveSubstoryGraph({
        graph: values[0],
        sub_story: values[1],
        callback,
      });
      setABTestInfo(abTestingInfo);
    })
    .catch(captureException);
}

export function getStepPriorities(botId) {
  const url = `${BASE_URL}get_node_priority/${botId}/`;
  const request = MogamboRequest(url, 'GET');

  fetch(request)
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.indexOf('application/json') !== -1) {
        return response.json().then((json) => {
          authenticate(json);
          receiveStepPriorities(json.response);
        });
      }

      resetData();
      throw new Error('API request failed!');
    })
    .catch(captureException);
}

export function getAllUserResponses(nodeId, currentLanguage, callback) {
  const url =
    `${BASE_URL}node/user_responses/${nodeId}/v2/` +
    `?lang_src=${currentLanguage.languageSource}&lang_script=${currentLanguage.languageScript}`;
  const request = MogamboRequest(url, 'GET');

  fetch(request)
    .then((response) => {
      return validateResponse(response);
    })
    .then((json) => {
      if (authenticate(json)) {
        if (json.error) {
          showRequestFailureAlert(
            json.error || 'Error occured while fetching all user responses'
          );
        } else {
          receiveUserResponses(json, callback);
        }
      }
    })
    .catch(() => {
      showRequestFailureAlert(
        'Error occured while fetching all user responses'
      );
    });
}

/**
 * This function fetches the Disambiguation Texts of different enabled languages.
 * @param {string} domainId
 * @param {string} sentenceId
 * @param {Function} callback
 */
export const getDisambiguationTexts = (domainId, sentenceId, callback) => {
  const url = `${BASE_URL}domains/${domainId}/disambiguation/${sentenceId}/${'?version=v2'}`;
  mogamboRequestV3(url, 'GET').then(({ response }) => {
    if (response.error) {
      createAlert(response.error, null, AlertTypes.NEGATIVE);
      return;
    }
    receiveDisambiguationTexts(response.body, callback);
  });
};

export function getGeneralResponses(botId, callback) {
  const url = `${BASE_URL}general_responses/${botId}/`;
  const request = MogamboRequest(url, 'GET');

  fetch(request)
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.indexOf('application/json') !== -1) {
        return response.json().then((json) => {
          authenticate(json);
          receiveGeneralResponses(json.response);
          if (callback) callback();
        });
      }

      throw new Error('API request failed!');
    })
    .catch(captureException);
}

export function getBotExtract(domainTitle) {
  const url = `${BASE_URL}domain_extract/${domainTitle}/`;
  const request = MogamboRequest(url, 'GET');

  fetch(request)
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.indexOf('application/json') !== -1) {
        return response.json().then((json) => {
          receiveBotExtract(domainTitle, json);
        });
      }

      throw new Error('API request failed!');
    })
    .catch(captureException);
}

/*
    POST CALLS
*/

export function createNewBot(data) {
  const url = `${BASE_URL}create_new_domain/`;
  const request = MogamboRequest(url, 'POST', data);

  fetch(request)
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.indexOf('application/json') !== -1) {
        return response.json().then((json) => {
          authenticate(json);
          if (json.success) {
            receiveNewBot(json.body);
          } else {
            showRequestFailureAlert(json.error || 'Error creating new bot');
          }
        });
      }

      throw new Error('API request failed!');
    })
    .catch(captureException);
}

export function createNewStory(data, callback) {
  const url = `${BASE_URL}create_new_story/`;
  const headers = {
    'x-haptik-bot-name': data.domain_title,
  };
  const request = MogamboRequest(url, 'POST', data, headers);

  fetch(request)
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.indexOf('application/json') !== -1) {
        return response.json().then((json) => {
          authenticate(json);
          receiveNewStory(json.response);

          if (callback) callback(json.response);
        });
      }

      throw new Error('API request failed!');
    })
    .catch(captureException);
}

export function createNewSubstory(
  data,
  storyId,
  domainId,
  additionalHeaders,
  callback,
  isEdit
) {
  const url = `${BASE_URL}create_new_substory/`;
  const request = MogamboRequest(url, 'POST', data, additionalHeaders);

  fetch(request)
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.indexOf('application/json') !== -1) {
        return response.json().then((json) => {
          authenticate(json);
          getSubstoryGraph(storyId);
          if (callback) callback(json.response);
        });
      }

      // true indicates error
      if (callback) callback(true);
      throw new Error('API request failed!');
    })
    .catch(captureException);
}

export function createNewStep(data, subStoryId, storyId, callback) {
  const url = `${BASE_URL}create_new_node/`;
  const headers = {
    'x-haptik-bot-name': data.stepInfo.domain_name,
  };
  const request = MogamboRequest(url, 'POST', data, headers);

  fetch(request)
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.indexOf('application/json') !== -1) {
        response.json().then((json) => {
          authenticate(json);
          if (json.status) {
            callback(false, json.response);
            receiveNewStep(json.response);
          } else {
            callback(true);
            showRequestFailureAlert('Create step failed!');
          }
        });
      } else {
        callback(true);
        showRequestFailureAlert('Create step failed!');
      }
    })
    .catch(captureException);
}

export function sendGeneralResponses(data) {
  const url = `${BASE_URL}create_generic_response/`;
  const request = MogamboRequest(url, 'POST', data);

  fetch(request)
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.indexOf('application/json') !== -1) {
        response.json().then((json) => {
          authenticate(json);
          receiveGeneralResponsesPOST(json.response, false);
        });
      }

      console.log('API request failed!');
      receiveGeneralResponsesPOST(null, true);
    })
    .catch(captureException);
}

export function sendUserResponses(data, stepId, callback, domainTitle) {
  const url = `${BASE_URL}node/user_responses/${stepId}/v2/`;
  const headers = {
    'x-haptik-bot-name': domainTitle,
  };
  const request = MogamboRequest(url, 'POST', data, headers);

  return fetch(request)
    .then((response) => {
      return validateResponse(response);
    })
    .then((json) => {
      if (authenticate(json)) {
        if (json.error) {
          showRequestFailureAlert(
            json.error || 'Error while saving all user responses.'
          );
        } else {
          receiveUserResponses(json, callback);
        }
      }
    })
    .catch((exception) => {
      showRequestFailureAlert('Error while saving all user responses');
      captureException(exception);
    });
}

export function saveDisambiguationTexts(domainId, sentenceId, data, callback) {
  const url = `${BASE_URL}domains/${domainId}/disambiguation/${sentenceId}/${'?version=v2'}`;
  mogamboRequestV3(url, 'POST', data).then(({ response }) => {
    if (response.error) {
      createAlert(response.error, null, AlertTypes.NEGATIVE);
      return;
    }
    createAlert(
      'Disambiguation Texts Saved Successfully',
      null,
      AlertTypes.POSITIVE
    );
    receiveDisambiguationTexts(response.body, callback);
  });
}

export function sendStepPriorities(data) {
  const url = `${BASE_URL}create_node_priority/`;
  const request = MogamboRequest(url, 'POST', data);

  fetch(request)
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.indexOf('application/json') !== -1) {
        response.json().then((json) => {
          authenticate(json);
          receiveStepPrioritiesPOST(json.response, false);
        });
      }

      console.log('API request failed!');
      receiveStepPrioritiesPOST(null, true);
    })
    .catch(captureException);
}

export function transferBot(domainTitle, additionalHeaders, callback) {
  const url = `${BASE_URL}transfer_domain/v2/${domainTitle}/`;
  const addHeaders = { ...additionalHeaders };

  if (!csrfSafeMethod('POST') && sameOrigin(url)) {
    addHeaders['X-CSRFToken'] = getCookie('csrftoken');
  }

  const request = MogamboRequest(url, 'POST', undefined, addHeaders);

  fetch(request)
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (
        response.status === 200 &&
        contentType &&
        contentType.indexOf('application/json') !== -1
      ) {
        response.json().then((json) => {
          authenticate(json);
          receiveTransferBot(json.response);

          if (callback) {
            callback();
          }
        });

        return;
      }

      throw new Error('API request failed!');
    })
    .catch(captureException);
}

export function deployBot(
  domainName,
  additionalHeaders,
  domainId,
  data = {},
  callback = () => {}
) {
  const url = `${BASE_URL}domain/deploy`;
  const request = MogamboRequest(
    url,
    'POST',
    {
      domain_name: domainName,
      ...data,
    },
    additionalHeaders
  );
  fetch(request)
    .then((response) => {
      return validateResponse(response);
    })
    .then((json) => {
      if (authenticate(json)) {
        if (json.error) {
          showRequestFailureAlert(
            json.error || 'Error occured while deploying bot'
          );
        } else {
          receiveDeployBot(json.body, data.action);
          callback();
        }
      }
    })
    .catch((exception) => {
      showRequestFailureAlert('Error occured while deploying bot');
      captureException(exception);
    });
}

export function startBotAugmentation(domainName, additionalHeaders) {
  const url = `${BASE_URL}domain/deploy`;
  const request = MogamboRequest(
    url,
    'POST',
    {
      domain_name: domainName,
      evaluate_paraphrases: true,
    },
    additionalHeaders
  );
  fetch(request)
    .then((response) => {
      return validateResponse(response);
    })
    .then((json) => {
      if (authenticate(json)) {
        if (json.error) {
          showRequestFailureAlert(
            json.error || 'Error occured while adding smart variants.'
          );
        }
      }
    })
    .catch((exception) => {
      showRequestFailureAlert('Error occured while adding smart variants.');
      captureException(exception);
    });
}

export function uploadBot(data) {
  const url = `${BASE_URL}update_domain_extract/`;
  const request = MogamboRequest(url, 'POST', data);

  fetch(request)
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.indexOf('application/json') !== -1) {
        return response.json().then((json) => {
          authenticate(json);
          receiveUpdateBotPOST(json.response);
        });
      }

      throw new Error('API request failed!');
    })
    .catch(captureException);
}

export function uploadNewBot(data) {
  const url = `${BASE_URL}dev_to_staging/`;
  const request = MogamboRequest(url, 'POST', data);

  fetch(request)
    .then((response) => {
      if (response.status === 200) {
        response.json().then((json) => {
          if (json.status) {
            authenticate(json);
          }
          alert(json.response);
          setTimeout(() => {
            receiveUpdateBotPOST();
          }, 250);
        });
      }
      alert('Uploading new bot failed. Please see server error logs.');
      setTimeout(() => {
        receiveUpdateBotPOST();
      }, 250);
    })
    .catch(captureException);
}

export function getGeneralNodes(botId, storyId, currentLanguage) {
  const isSelfServe = BotStore.getSelfServeLayout();

  let url = `${BASE_URL}/node/general_nodes/?domain_id=${botId}`;

  if (!isSelfServe) {
    url += `&lang_src=${currentLanguage.languageSource}&lang_script=${currentLanguage.languageScript}`;
  }

  if (storyId && storyId !== '') {
    url = `${url}&story_id=${storyId}`;
  }

  url = appendVersionToURL(url, 3);

  const request = MogamboRequest(url, 'GET');

  return fetch(request)
    .then((response) => {
      return validateResponse(response);
    })
    .then((json) => {
      if (authenticate(json)) {
        if (json.error) {
          showRequestFailureAlert(
            json.error || 'Error fetching General Nodes. Please try again.'
          );
        } else {
          receivedGeneralNodes(json.body);
        }
      }
    })
    .catch((exception) => {
      showRequestFailureAlert(`Error fetching General Nodes.${exception}`);
      captureException(exception);
    });
}

export function downloadFAQNodes(botId) {
  const url = `${BASE_URL}domains/${botId}/bulk_actions/?action=faqs`;
  return mogamboRequestV3(url, 'GET');
}

export function uploadFAQNodes(botId, data) {
  const url = `${BASE_URL}domains/${botId}/bulk_actions/?action=faqs`;
  return mogamboRequestV3(url, 'POST', data);
}

export function downloadBotResponses(botId) {
  const url = `${BASE_URL}domains/${botId}/bulk_actions/?action=bot_says`;
  return mogamboRequestV3(url, 'GET');
}

export function downloadBotSynonyms(botId) {
  const url = `${BASE_URL}bot_synonyms/${botId}/`;
  return mogamboRequestV3(url, 'GET');
}

export function uploadBotResponses(botId, data) {
  const url = `${BASE_URL}domains/${botId}/bulk_actions/?action=bot_says`;
  return mogamboRequestV3(url, 'POST', data);
}

export function uploadBotSynonyms(botId, data) {
  const url = `${BASE_URL}bot_synonyms/${botId}/`;
  return mogamboRequestV3(url, 'POST', data);
}

export function getBulkActionsStatus(botId) {
  const url = `${BASE_URL}domains/${botId}/bulk_actions/status/`;
  return mogamboRequestV3(url, 'GET');
}

export function saveGeneralNodesApi(
  data,
  botId,
  storyId,
  currentLanguage,
  callback = () => null,
  domainTitle
) {
  const isSelfServe = BotStore.getSelfServeLayout();
  let url = `${BASE_URL}/node/general_nodes/?`;

  const currentData = data.pending.splice(0, 1);

  const headers = {
    'x-haptik-bot-name': domainTitle,
  };

  url = appendVersionToURL(url, 3);

  const request = MogamboRequest(
    url,
    'POST',
    {
      data: currentData,
      domain_id: botId,
      story_id: storyId,
      lang_src: isSelfServe ? currentLanguage : currentLanguage.languageSource,
      lang_script: isSelfServe
        ? currentLanguage
        : currentLanguage.languageScript,
    },
    headers
  );

  fetch(request)
    .then((response) => {
      return validateResponse(response);
    })
    .then((json) => {
      if (authenticate(json)) {
        if (json.error) {
          showRequestFailureAlert(
            json.error ||
              'There was an Unknown error saving some of your General Step changes.'
          );
        } else {
          // If API failed
          // => Show error notification
          // => Add remaining data to Failed group
          // -> Update UI
          // => End Flow

          // Else if API success and Pending node_data
          // => Add to success list
          // => Update loading indicator
          // => Next API call

          // Else if API success and No Pending node_data
          // => Add to success list
          // => Update UI
          // => End Flow

          data.failure = data.failure.concat(json.body.failure);
          data.success = data.success.concat(json.body.success);

          if (json.body.failure.length > 0) {
            // Failure: Show alert and update with success/failed data
            data.failure = data.failure.concat(data.pending);
            showRequestFailureAlert(
              json.body.errors ||
                'There was an error saving your General Step Changes.'
            );
            updatedGeneralNodes(data.success, data.failure);
            callback(false);
          } else if (data.pending.length > 0) {
            // Success: Continue with other pending data
            saveGeneralNodes(
              data,
              botId,
              storyId,
              currentLanguage,
              callback,
              domainTitle
            );
          } else {
            // Success and No More Pending tasks
            updatedGeneralNodes(data.success, data.failure);
            callback(true);
          }
        }
      }
    })
    .catch((exception) => {
      showRequestFailureAlert(
        'There was an error saving your General Step changes.'
      );
      captureException(exception);
      callback(false);
    });
}

export function getNodeSummary(nodeId) {
  const url = `${BASE_URL}get_node_info/?nodes=${nodeId}`;
  const request = MogamboRequest(url, 'GET');

  fetch(request)
    .then((response) => {
      return validateResponse(response);
    })
    .then((json) => {
      if (authenticate(json)) {
        if (json.error) {
          showRequestFailureAlert(
            json.error || "Error fetching Node's summary information."
          );
        } else {
          receivedNodeSummary(json.response);
        }
      }
    })
    .catch((exception) => {
      showRequestFailureAlert(
        `Error fetching Node's summary information.${exception}`
      );
      captureException(exception);
    });
}

export function getUserInfo() {
  const URL = `${BASE_URL}get_user_info/`;
  return mogamboRequestV2(URL, 'GET');
}

export function search(params) {
  const URL = `${BASE_URL}search/`;
  return mogamboRequestV2(URL, 'POST', params);
}

export const getUserPartnerList = () => {
  const url = `${ADMIN_BASE_URL}expert/partner/`;
  return mogamboRequestV3(url, 'GET');
};

export const API = {
  businessV2,
  botGlossary,
  globalConstants,
};
